import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  PlusOutlined,
  UnlockOutlined
} from "@ant-design/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Button, Input, Modal, Spin, Table, Tooltip, message } from "antd";
import {
  clearSprSotrPassword,
  deleteSprSotr,
  fetchSprSotrList,
  setSprSotrPassword
} from "./spr-sotr-api";
import SprSotr from "./spr-sotr";
import SprSotrPasswordModal from "./spr-sotr-password-modal";
import "./spr-sotrs.css";

const pick = (row, ...keys) => {
  for (const key of keys) {
    if (row?.[key] !== undefined && row?.[key] !== null) return row[key];
  }
  return undefined;
};

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const toFlag = (value) => Number(value) === 1;

const normalizeRow = (row) => ({
  idSotr: toNumber(pick(row, "idSotr", "IdSotr")),
  name: pick(row, "name", "Name") ?? "",
  fullName: pick(row, "fullName", "FullName") ?? "",
  dolj: pick(row, "dolj", "Dolj") ?? "",
  komment: pick(row, "komment", "Komment") ?? "",
  isDel: toFlag(pick(row, "isDel", "IsDel")),
  hasPassword: toFlag(pick(row, "hasPassword", "HasPassword"))
});

const qFromSearchParams = (params) => {
  const src =
    params instanceof URLSearchParams ? params : new URLSearchParams(params || "");
  return src.has("q") ? src.get("q") ?? "" : "";
};

/**
 * Список сотрудников (SprSotrs).
 */
const SprSotrs = () => {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = routeId === "new";
  const editId = routeId && /^\d+$/.test(routeId) ? Number(routeId) : null;
  const drawerOpen = isNew || editId != null;

  const urlQ = useMemo(() => qFromSearchParams(searchParams), [searchParams]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nameFilter, setNameFilter] = useState(() => urlQ);
  const [selectedId, setSelectedId] = useState(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const requestIdRef = useRef(0);
  const skipUrlSyncRef = useRef(false);

  useEffect(() => {
    const q = (nameFilter || "").trim();
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (searchParams.toString() === next.toString()) return;
    skipUrlSyncRef.current = true;
    setSearchParams(next, { replace: true });
  }, [nameFilter, searchParams, setSearchParams]);

  useEffect(() => {
    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }
    const fromUrl = qFromSearchParams(searchParams);
    setNameFilter((prev) => (prev === fromUrl ? prev : fromUrl));
  }, [searchParams]);

  const loadItems = useCallback(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");
    fetchSprSotrList()
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setRows((Array.isArray(data) ? data : []).map(normalizeRow).filter((r) => r.idSotr != null));
      })
      .catch((e) => {
        if (requestId !== requestIdRef.current) return;
        setError(e.message || "Не удалось загрузить сотрудников");
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (editId != null) setSelectedId(editId);
  }, [editId]);

  useEffect(() => {
    if (editId == null || loading || !rows.length) return;
    if (!rows.some((r) => r.idSotr === editId)) {
      navigate("/spr/sotr", { replace: true });
    }
  }, [editId, rows, loading, navigate]);

  const filteredRows = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        String(r.name || "")
          .toLowerCase()
          .includes(q) ||
        String(r.fullName || "")
          .toLowerCase()
          .includes(q) ||
        String(r.dolj || "")
          .toLowerCase()
          .includes(q)
    );
  }, [rows, nameFilter]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.idSotr === selectedId) || null,
    [rows, selectedId]
  );

  const openEdit = useCallback(
    (record) => {
      if (record?.idSotr == null) return;
      setSelectedId(record.idSotr);
      navigate(`/spr/sotr/${record.idSotr}`);
    },
    [navigate]
  );

  const openCreate = useCallback(() => {
    navigate("/spr/sotr/new");
  }, [navigate]);

  const closeDrawer = useCallback(() => {
    navigate("/spr/sotr");
  }, [navigate]);

  const onSaved = useCallback(
    (saved) => {
      loadItems();
      const newId = toNumber(pick(saved || {}, "idSotr", "IdSotr"));
      if (isNew && newId != null) {
        navigate(`/spr/sotr/${newId}`, { replace: true });
      }
    },
    [isNew, loadItems, navigate]
  );

  const onDelete = useCallback(() => {
    if (selectedRow?.idSotr == null) return;
    const id = selectedRow.idSotr;
    const title = selectedRow.isDel ? "Снять пометку на удаление?" : "Удалить сотрудника?";
    const content = selectedRow.isDel
      ? selectedRow.name || `№${id}`
      : `${selectedRow.name || `№${id}`}. При наличии ссылок будет пометка на удаление.`;

    Modal.confirm({
      title,
      content,
      okText: selectedRow.isDel ? "Снять" : "Удалить",
      cancelText: "Отмена",
      okButtonProps: selectedRow.isDel ? undefined : { danger: true },
      onOk: () =>
        deleteSprSotr(id)
          .then(() => {
            message.success(selectedRow.isDel ? "Пометка снята" : "Удалено");
            if (editId === id) navigate("/spr/sotr");
            if (selectedId === id) setSelectedId(null);
            loadItems();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось удалить");
            return Promise.reject(e);
          })
    });
  }, [selectedRow, editId, selectedId, loadItems, navigate]);

  const onClearPassword = useCallback(() => {
    if (selectedRow?.idSotr == null) return;
    Modal.confirm({
      title: "Сбросить пароль?",
      content: selectedRow.name || `№${selectedRow.idSotr}`,
      okText: "Сбросить",
      cancelText: "Отмена",
      onOk: () =>
        clearSprSotrPassword(selectedRow.idSotr)
          .then(() => {
            message.success("Пароль сброшен");
            loadItems();
          })
          .catch((e) => {
            message.error(e.message || "Не удалось сбросить пароль");
            return Promise.reject(e);
          })
    });
  }, [selectedRow, loadItems]);

  const onPasswordOk = useCallback(
    async (password) => {
      if (selectedRow?.idSotr == null) return;
      setPasswordSaving(true);
      try {
        await setSprSotrPassword(selectedRow.idSotr, password);
        message.success("Пароль установлен");
        setPasswordOpen(false);
        loadItems();
      } catch (e) {
        message.error(e.message || "Не удалось установить пароль");
        throw e;
      } finally {
        setPasswordSaving(false);
      }
    },
    [selectedRow, loadItems]
  );

  const columns = useMemo(
    () => [
      {
        title: "",
        label: "Статус",
        key: "status",
        width: 44,
        align: "center",
        render: (_, record) => {
          const deleted = Boolean(record.isDel);
          const label = deleted ? "Помечен на удаление" : "Активен";
          return (
            <Tooltip title={label}>
              <span
                className="spr-sotrs__status-dot"
                data-tone={deleted ? "off" : "on"}
                aria-label={label}
              />
            </Tooltip>
          );
        }
      },
      {
        title: "#",
        dataIndex: "idSotr",
        key: "idSotr",
        width: 70
      },
      {
        title: "Имя",
        dataIndex: "name",
        key: "name",
        width: 140,
        ellipsis: true
      },
      {
        title: "Полное ФИО",
        dataIndex: "fullName",
        key: "fullName",
        width: 200,
        ellipsis: true
      },
      {
        title: "Должность",
        dataIndex: "dolj",
        key: "dolj",
        width: 140,
        ellipsis: true
      },
      {
        title: "Комментарий",
        dataIndex: "komment",
        key: "komment",
        width: 160,
        ellipsis: true
      }
    ],
    []
  );

  return (
    <div className="spr-sotrs">
      {error ? <Alert className="spr-sotrs__error" type="error" showIcon message={error} /> : null}
      <div className="spr-sotrs__body">
        <div className="spr-sotrs__list">
          <div className="spr-sotrs__toolbar">
            <div className="spr-sotrs__extra-actions">
              <Tooltip title="Создать">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  aria-label="Создать"
                  onClick={openCreate}
                />
              </Tooltip>
              <Tooltip title={selectedRow ? "Изменить" : "Выберите запись"}>
                <span>
                  <Button
                    icon={<EditOutlined />}
                    disabled={!selectedRow}
                    aria-label="Изменить"
                    onClick={() => selectedRow && openEdit(selectedRow)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Пароль">
                <span>
                  <Button
                    icon={<KeyOutlined />}
                    disabled={!selectedRow}
                    aria-label="Пароль"
                    onClick={() => setPasswordOpen(true)}
                  />
                </span>
              </Tooltip>
              <Tooltip title="Сброс пароля">
                <span>
                  <Button
                    icon={<UnlockOutlined />}
                    disabled={!selectedRow}
                    aria-label="Сброс пароля"
                    onClick={onClearPassword}
                  />
                </span>
              </Tooltip>
              <Tooltip title={selectedRow ? "Удалить" : "Выберите запись"}>
                <span>
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    disabled={!selectedRow}
                    aria-label="Удалить"
                    onClick={onDelete}
                  />
                </span>
              </Tooltip>
            </div>
            <Input
              allowClear
              variant="borderless"
              className="spr-sotrs__filter"
              placeholder="Фильтр по имени, ФИО или должности"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
            />
          </div>
          <div className="spr-sotrs__list-body">
            {loading && !rows.length ? (
              <div className="spr-sotrs__center">
                <Spin tip="Загрузка сотрудников..." />
              </div>
            ) : (
              <Table
                className="spr-sotrs__table"
                size="small"
                rowKey="idSotr"
                columns={columns}
                dataSource={filteredRows}
                loading={loading}
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    setSelectedId(record.idSotr);
                    openEdit(record);
                  }
                })}
                rowClassName={(record) =>
                  [
                    "spr-sotrs__row",
                    record.isDel ? "spr-sotrs__row--deleted" : "",
                    selectedId != null && record.idSotr === selectedId
                      ? "spr-sotrs__row--selected"
                      : "",
                    editId != null && record.idSotr === editId ? "spr-sotrs__row--editing" : ""
                  ]
                    .filter(Boolean)
                    .join(" ")
                }
                scroll={{ x: 720 }}
                locale={{
                  emptyText: nameFilter.trim()
                    ? "Нет сотрудников по фильтру"
                    : "Нет сотрудников"
                }}
              />
            )}
          </div>
        </div>
      </div>
      <SprSotr
        open={drawerOpen}
        idSotr={editId}
        isNew={isNew}
        onClose={closeDrawer}
        onSaved={onSaved}
      />
      <SprSotrPasswordModal
        open={passwordOpen}
        loading={passwordSaving}
        onCancel={() => setPasswordOpen(false)}
        onOk={onPasswordOk}
      />
    </div>
  );
};

export default SprSotrs;
